import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { X, Settings2, AlertCircle } from 'lucide-react';

export default function SepaPreSignCustomizeModal({ montant40Cents: _montant40Cents, montantTotalTTC, splitOverride, refRCDS, initial, onClose, onApply }) {
  const initNb = initial?.nb_echeances || 3;
  const initOffsetsArr = initial?.offsets_days || [30, 60, 90, 120].slice(0, initNb);
  const initAmountsArr = initial?.amounts_cents
    ? initial.amounts_cents.map((c) => (c / 100).toFixed(2))
    : null;
  const initRef = initial?.reference || refRCDS;

  // V35.42 — Split acompte / SEPA
  const initSepaCents = splitOverride?.montant_sepa_cents ?? Math.round(montantTotalTTC * 0.4 * 100);
  const initSepaPct = montantTotalTTC > 0 ? (initSepaCents / 100 / montantTotalTTC) * 100 : 40;

  // Génère N échéances équiréparties à partir d'un nb / offset de base / montant total
  const buildLines = (nb, baseOffsets, totalEur, customAmounts) => {
    const result = [];
    const baseCents = Math.floor((totalEur * 100) / nb);
    const restCents = Math.round(totalEur * 100) - baseCents * nb;
    for (let i = 0; i < nb; i++) {
      const offset = baseOffsets && baseOffsets[i] !== undefined ? baseOffsets[i] : (i + 1) * 30;
      const amount = customAmounts && customAmounts[i] !== undefined
        ? customAmounts[i]
        : ((baseCents + (i === 0 ? restCents : 0)) / 100).toFixed(2);
      result.push({ offset: String(offset), amount: String(amount) });
    }
    return result;
  };

  const [values, setValues] = useState({
    nb_echeances: String(initNb),
    reference: initRef,
    sepa_pct: initSepaPct.toFixed(2),
    sepa_eur: (initSepaCents / 100).toFixed(2),
    split_mode: 'percent',
    amounts_auto: !initAmountsArr, // Si pas de montants fournis : répartition auto active
  });
  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const [lines, setLines] = useState(() =>
    buildLines(initNb, initOffsetsArr, initSepaCents / 100, initAmountsArr)
  );

  // Synchronisation % ↔ € pour le split
  const handlePctChange = (v) => {
    const pct = Math.max(0, Math.min(100, Number(v) || 0));
    const eur = (montantTotalTTC * pct / 100).toFixed(2);
    setValues((s) => ({ ...s, sepa_pct: v, sepa_eur: eur }));
  };
  const handleEurChange = (v) => {
    const eur = Math.max(0, Math.min(montantTotalTTC, Number(v) || 0));
    const pct = montantTotalTTC > 0 ? ((eur / montantTotalTTC) * 100).toFixed(2) : '0';
    setValues((s) => ({ ...s, sepa_eur: v, sepa_pct: pct }));
  };

  const sepaCentsFinal = Math.round(Number(values.sepa_eur || 0) * 100);
  const acompteEur = montantTotalTTC - sepaCentsFinal / 100;
  const acomptePct = montantTotalTTC > 0 ? (acompteEur / montantTotalTTC) * 100 : 0;
  const splitValid = sepaCentsFinal > 0 && sepaCentsFinal <= montantTotalTTC * 100;
  const totalEUR = sepaCentsFinal / 100;
  const nb = Number(values.nb_echeances) || 0;

  // Recalcule les lignes quand nb d'échéances change OU quand le solde SEPA change (mode auto)
  useEffect(() => {
    if (values.amounts_auto) {
      // Conserve les offsets existants, redistribue le montant
      setLines((prev) => buildLines(
        nb,
        prev.map((l) => Number(l.offset) || 30),
        totalEUR,
        null
      ));
    } else if (nb !== lines.length) {
      // Si le nb change mais qu'on est en mode manuel, ajuste juste le nombre de lignes
      setLines((prev) => buildLines(
        nb,
        prev.map((l) => Number(l.offset) || 30),
        totalEUR,
        prev.map((l) => l.amount)
      ));
    }
  }, [nb, totalEUR, values.amounts_auto]); // eslint-disable-line

  const updateLine = (idx, key, val) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [key]: val } : l)));
  };

  // Presets d'offsets
  const applyPreset = (offsets) => {
    setLines((prev) => prev.map((l, i) => ({
      ...l,
      offset: String(offsets[i] !== undefined ? offsets[i] : (i + 1) * 30),
    })));
  };

  // Aujourd'hui pour le calcul des dates affichées
  const today = new Date();
  const fmtDateFR = (offsetDays) => {
    const dt = new Date(today);
    dt.setDate(dt.getDate() + Number(offsetDays || 0));
    return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const offsetsParsed = lines.map((l) => Number(l.offset)).filter(Number.isFinite);
  const amountsParsed = lines.map((l) => Number(l.amount)).filter(Number.isFinite);
  const sumAmounts = amountsParsed.reduce((a, b) => a + b, 0);
  const allOffsetsOk = offsetsParsed.length === nb && offsetsParsed.every((x) => x > 0);
  const allAmountsOk = amountsParsed.length === nb && amountsParsed.every((x) => x > 0);
  const amountMismatch = !values.amounts_auto && allAmountsOk && Math.abs(sumAmounts - totalEUR) > 0.01;
  const valid = nb >= 1 && nb <= 12 && allOffsetsOk && allAmountsOk && !amountMismatch && splitValid;

  const handleApply = () => {
    const echeancier = {
      nb_echeances: nb,
      offsets_days: offsetsParsed,
      reference: (values.reference || '').trim() || refRCDS,
    };
    if (!values.amounts_auto && allAmountsOk && !amountMismatch) {
      echeancier.amounts_cents = amountsParsed.map((eur) => Math.round(eur * 100));
    }
    const split = {
      montant_sepa_cents: sepaCentsFinal,
      pct_sepa: Number(values.sepa_pct),
    };
    onApply({ echeancier, split });
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="sepa-pre-sign-customize-modal" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-[#0a3d62] to-[#0c4a78] p-5 flex items-center justify-between text-white">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#f5cc3e]">Super Admin · Avant signature</div>
            <h3 className="text-xl font-bold mt-1">Personnaliser l'échéancier SEPA</h3>
            <p className="text-xs text-white/70 mt-1">Total contrat : <span className="font-bold tabular-nums">{montantTotalTTC.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span> · Total SEPA : <span className="font-bold tabular-nums text-[#f5cc3e]">{totalEUR.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span></p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10" data-testid="sepa-pre-sign-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* V35.42 — Répartition acompte / SEPA */}
          <div className="p-3 rounded-lg border-2 border-[#DEA900]/30 bg-[#DEA900]/5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-[#0a3d62] uppercase tracking-wider">Répartition acompte / SEPA</p>
              <div className="flex bg-white border border-slate-200 rounded-md p-0.5 text-[10px]">
                <button
                  type="button"
                  onClick={() => set('split_mode', 'percent')}
                  className={`px-2 py-1 rounded font-semibold ${values.split_mode === 'percent' ? 'bg-[#0a3d62] text-white' : 'text-slate-600'}`}
                  data-testid="sepa-split-mode-percent"
                >% Pourcentage</button>
                <button
                  type="button"
                  onClick={() => set('split_mode', 'amount')}
                  className={`px-2 py-1 rounded font-semibold ${values.split_mode === 'amount' ? 'bg-[#0a3d62] text-white' : 'text-slate-600'}`}
                  data-testid="sepa-split-mode-amount"
                >€ Montant</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-semibold text-slate-600 uppercase">Acompte virement (60% défaut)</label>
                <div className="mt-1 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <p className="text-sm font-bold text-emerald-700 tabular-nums">{acompteEur.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</p>
                  <p className="text-[10px] text-emerald-600">{acomptePct.toFixed(2)} % du contrat</p>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-600 uppercase">Solde SEPA (40% défaut)</label>
                {values.split_mode === 'percent' ? (
                  <div className="mt-1 relative">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={0.5}
                      value={values.sepa_pct}
                      onChange={(e) => handlePctChange(e.target.value)}
                      className="w-full pl-3 pr-8 py-2 border border-slate-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-[#DEA900] outline-none"
                      data-testid="sepa-split-pct-input"
                    />
                    <span className="absolute right-3 top-2 text-slate-500 text-sm">%</span>
                  </div>
                ) : (
                  <div className="mt-1 relative">
                    <input
                      type="number"
                      min={0}
                      max={montantTotalTTC}
                      step={0.01}
                      value={values.sepa_eur}
                      onChange={(e) => handleEurChange(e.target.value)}
                      className="w-full pl-3 pr-8 py-2 border border-slate-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-[#DEA900] outline-none"
                      data-testid="sepa-split-eur-input"
                    />
                    <span className="absolute right-3 top-2 text-slate-500 text-sm">€</span>
                  </div>
                )}
                <p className="text-[10px] text-slate-500 mt-1 tabular-nums">
                  ≈ {values.split_mode === 'percent' ? `${Number(values.sepa_eur).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €` : `${Number(values.sepa_pct).toFixed(2)} %`} · à prélever via SEPA
                </p>
              </div>
            </div>
            {!splitValid && (
              <p className="text-[11px] text-rose-700 font-semibold mt-2">Le solde SEPA doit être supérieur à 0 et inférieur ou égal au total contrat.</p>
            )}
          </div>

          <div>
            <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Nombre d'échéances (1 à 12)</label>
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => set('nb_echeances', String(Math.max(1, nb - 1)))}
                className="h-9 w-9 rounded-lg border-2 border-slate-300 text-slate-600 hover:bg-slate-100 font-bold"
                data-testid="sepa-pre-sign-nb-minus"
              >−</button>
              <input
                type="number"
                min={1}
                max={12}
                value={values.nb_echeances}
                onChange={(e) => set('nb_echeances', e.target.value)}
                className="flex-1 px-3 py-2 border-2 border-slate-300 rounded-lg text-center font-bold text-lg focus:ring-2 focus:ring-[#DEA900] outline-none"
                data-testid="sepa-pre-sign-nb"
              />
              <button
                type="button"
                onClick={() => set('nb_echeances', String(Math.min(12, nb + 1)))}
                className="h-9 w-9 rounded-lg border-2 border-slate-300 text-slate-600 hover:bg-slate-100 font-bold"
                data-testid="sepa-pre-sign-nb-plus"
              >+</button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <span className="text-[10px] uppercase text-slate-500 font-semibold mr-1 self-center">Presets :</span>
              {[
                { label: '3 mens. (J+30/60/90)', offsets: [30, 60, 90], nb: 3 },
                { label: '4 mens. (J+30/60/90/120)', offsets: [30, 60, 90, 120], nb: 4 },
                { label: '6 mens. mensuelles', offsets: [30, 60, 90, 120, 150, 180], nb: 6 },
                { label: '12 mens. mensuelles', offsets: Array.from({ length: 12 }, (_, i) => (i + 1) * 30), nb: 12 },
              ].map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => {
                    set('nb_echeances', String(p.nb));
                    setTimeout(() => applyPreset(p.offsets), 0);
                  }}
                  className="px-2 py-1 text-[10px] rounded-md border border-slate-300 text-slate-700 hover:bg-[#DEA900] hover:text-[#0a3d62] hover:border-[#DEA900] transition-colors font-semibold"
                  data-testid={`sepa-preset-${p.nb}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tableau dynamique des échéances */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Détail des échéances</label>
              <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={values.amounts_auto}
                  onChange={(e) => set('amounts_auto', e.target.checked)}
                  className="rounded border-slate-300"
                  data-testid="sepa-amounts-auto-toggle"
                />
                Répartition automatique des montants
              </label>
            </div>
            <div className="border-2 border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-600 text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="px-2 py-2 text-left w-12">N°</th>
                    <th className="px-2 py-2 text-left">Offset (jours après mandat)</th>
                    <th className="px-2 py-2 text-left">Date prévisionnelle</th>
                    <th className="px-2 py-2 text-right">Montant €</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {lines.map((line, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-2 py-2 font-bold text-[#0a3d62]">#{idx + 1}</td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-slate-400">J +</span>
                          <input
                            type="number"
                            min={1}
                            max={730}
                            value={line.offset}
                            onChange={(e) => updateLine(idx, 'offset', e.target.value)}
                            className="w-20 px-2 py-1 border border-slate-300 rounded font-mono text-sm focus:ring-2 focus:ring-[#DEA900] outline-none"
                            data-testid={`sepa-line-offset-${idx}`}
                          />
                          <span className="text-[10px] text-slate-400">jours</span>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-slate-700 font-semibold whitespace-nowrap">
                        {fmtDateFR(line.offset)}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <div className="relative inline-block">
                          <input
                            type="number"
                            min={0}
                            step={0.01}
                            value={line.amount}
                            onChange={(e) => {
                              updateLine(idx, 'amount', e.target.value);
                              if (values.amounts_auto) set('amounts_auto', false);
                            }}
                            disabled={values.amounts_auto}
                            className={`w-28 pl-2 pr-6 py-1 border rounded font-mono text-sm text-right focus:ring-2 focus:ring-[#DEA900] outline-none ${values.amounts_auto ? 'bg-slate-100 border-slate-200 text-slate-500' : 'border-slate-300'}`}
                            data-testid={`sepa-line-amount-${idx}`}
                          />
                          <span className="absolute right-2 top-1 text-slate-400 text-xs">€</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                  <tr>
                    <td colSpan={3} className="px-2 py-2 text-right text-[11px] font-bold text-slate-700 uppercase">Somme :</td>
                    <td className={`px-2 py-2 text-right font-bold tabular-nums ${amountMismatch ? 'text-rose-700' : 'text-emerald-700'}`}>
                      {sumAmounts.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={3} className="px-2 py-1 text-right text-[10px] text-slate-500 uppercase">Doit égaler :</td>
                    <td className="px-2 py-1 text-right text-[11px] tabular-nums font-semibold text-slate-700">
                      {totalEUR.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {amountMismatch && (
              <p className="text-[11px] text-rose-700 font-semibold mt-1">
                ⚠️ La somme des montants ({sumAmounts.toFixed(2)} €) ne correspond pas au total SEPA ({totalEUR.toFixed(2)} €).
              </p>
            )}
          </div>

          <div>
            <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Référence affichée</label>
            <input
              type="text"
              value={values.reference}
              onChange={(e) => set('reference', e.target.value)}
              maxLength={140}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-[#DEA900] outline-none"
              data-testid="sepa-pre-sign-reference"
            />
          </div>

          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
            <AlertCircle className="inline h-4 w-4 mr-1.5" />
            Ces valeurs seront appliquées <strong>uniquement à la création du mandat</strong>. Elles n'affectent pas les souscriptions existantes.
          </div>
        </div>

        <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="sepa-pre-sign-cancel">Annuler</Button>
          <Button
            onClick={handleApply}
            disabled={!valid}
            className="bg-[#DEA900] hover:bg-[#f5cc3e] text-[#0a3d62] font-bold"
            data-testid="sepa-pre-sign-apply"
          >
            <Settings2 className="h-4 w-4 mr-2" />
            Appliquer la personnalisation
          </Button>
        </div>
      </div>
    </div>
  );
}
